import { Anchor, Box, Button } from '@mantine/core';
import { URLS } from 'consts';
import { usePostAuthLogin } from 'features/auth/api';
import { getZodSchemaErrorMessages } from 'helpers';
import { useServerErrors } from 'hooks';
import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TextInput, TextInputPassword } from 'shared/ui/Inputs';
import type { ValidationErrors } from 'types';
import { z } from 'zod';

import { AuthContainerLoginHeader } from '../../AuthContainerLoginHeader';
import { LoginOptionsDivider } from '../LoginOptionsDivider';
import { SsoLoginMethods } from '../SsoLoginMethods';

type Props = {
  identifier: string;
  setIdentifier: (identifier: string) => void;
  hasLoginMethodEmail: boolean;
  hasLoginMethodSms: boolean;
  switchLoginMethod: () => void;
  onSuccess: (accessToken: string) => void;
  backButtonAction?: () => void;
};

export const LoginPassword = ({
  identifier,
  setIdentifier,
  hasLoginMethodEmail,
  hasLoginMethodSms,
  switchLoginMethod,
  onSuccess,
  backButtonAction,
}: Props) => {
  const { t } = useTranslation(['common', 'user']);

  const schema = z.object({
    identifier: z
      .string()
      .trim()
      .min(1, { message: t('formErrors.required', { ns: 'common' }) }),
    password: z
      .string()
      .min(1, { message: t('formErrors.required', { ns: 'common' }) }),
  });

  const { getGeneralServerError, getServerErrorStatus } = useServerErrors();
  const identifierInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  const [password, setPassword] = useState('');
  const [errorStatus, setErrorStatus] = useState<ValidationErrors | null>(null);
  const [serverErrorMessage, setServerErrorMessage] = useState('');

  const { mutateAsync: login, isLoading } = usePostAuthLogin();

  const clearError = (field: string) => {
    setErrorStatus(status => ({
      ...status,
      [field]: '',
    }));
  };

  const handleChangeIdentifier = (e: ChangeEvent<HTMLInputElement>) => {
    clearError('identifier');
    clearError('email');
    clearError('phone');
    setIdentifier(e.target.value);
  };

  const handleChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    clearError('password');
    setPassword(e.target.value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors = getZodSchemaErrorMessages({
      schema,
      fields: { identifier, password },
    });
    const identifierErrorField = errors?.identifier;
    const passwordErrorField = errors?.password;

    if (errors) {
      setErrorStatus(status => ({
        ...status,
        identifier: errors.identifier,
        password: errors.password,
      }));
      if (identifierErrorField) {
        identifierInputRef.current?.focus();
      } else if (passwordErrorField) {
        passwordInputRef.current?.focus();
      }
    } else {
      try {
        const isPhoneIdentifier = !identifier.includes('@');
        const { accessToken } = await login({
          ...(isPhoneIdentifier
            ? { phone: identifier }
            : { email: identifier }),
          password,
        });

        onSuccess(accessToken);
      } catch (error) {
        const validationServerErrors = getServerErrorStatus(error);

        if (validationServerErrors) {
          setErrorStatus(validationServerErrors);
        } else {
          setServerErrorMessage(getGeneralServerError(error));
        }

        passwordInputRef.current?.focus();
      }
    }
  };

  return (
    <>
      <AuthContainerLoginHeader
        errorLabel={serverErrorMessage}
        backButtonAction={backButtonAction}
      />
      <form onSubmit={handleSubmit}>
        <Box
          component={TextInput}
          id="identifier"
          autoComplete="username"
          label={
            identifier.includes('@')
              ? t('email', { ns: 'common' })
              : t('phoneNumber', { ns: 'common' })
          }
          placeholder={t('emailPlaceholder', { ns: 'common' })}
          disabled
          value={identifier}
          error={
            errorStatus?.identifier || errorStatus?.email || errorStatus?.phone
          }
          ref={identifierInputRef}
          onChange={handleChangeIdentifier}
        />
        <TextInputPassword
          id="password"
          type="password"
          autoComplete="current-password"
          label={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {t('password', { ns: 'user' })}{' '}
              <Anchor
                component={Link}
                to={URLS.requestPasswordReset}
                state={{ email: identifier }}
              >
                {t('forgotPassword', { ns: 'user' })}
              </Anchor>
            </Box>
          }
          placeholder={t('passwordPlaceholder', { ns: 'user' })}
          autoFocus
          disabled={isLoading}
          value={password}
          error={errorStatus?.password}
          ref={passwordInputRef}
          onChange={handleChangePassword}
        />
        <Button
          sx={theme => ({
            marginBottom: theme.other.spacing(2),
            fontWeight: 600,
          })}
          type="submit"
          fullWidth
          size="lg"
          loading={isLoading}
        >
          {t('signIn', { ns: 'user' })}
        </Button>
      </form>
      {(hasLoginMethodEmail || hasLoginMethodSms) && (
        <Anchor
          component="button"
          sx={{
            width: '100%',
            textAlign: 'center',
          }}
          onClick={switchLoginMethod}
        >
          {t('wishOneTimePassword', { ns: 'user' })}
        </Anchor>
      )}
      <LoginOptionsDivider />
      <SsoLoginMethods />
    </>
  );
};
